import { forwardRef, useImperativeHandle, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Input, Select, Form } from 'antd';
import { useProcContext } from '@/entities/proc';
import { useNotificationContext } from '@/features/notifications';
import { useUpdateComponent } from '@/shared/api';
import { captureError } from '@/shared/utils/error-reporting';

type SelectOption = { label: string; value: string | null };

type SelectFormData = {
  options: Array<SelectOption>;
  placeholder: string;
  selectedOptionValue: string;
};

export const SelectEditForm = forwardRef((_, ref) => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotificationContext();
  const { selectedComponent, selectedStep, proc, setSelectedComponent } =
    useProcContext();
  const { mutate: updateComponent, isLoading: updateComponentLoading } =
    useUpdateComponent();

  const {
    id,
    component,
    componentName,
    options: defaultOptions,
    placeholder: defaultPlaceholder,
    value: defaultValue,
  } = selectedComponent ?? {};

  const [options, setOptions] = useState<Array<SelectOption>>(
    defaultOptions ?? [],
  );
  const [placeholder, setPlaceholder] = useState<string>(
    defaultPlaceholder ?? '',
  );
  const [selectedOptionValue, setSelectedOptionValue] = useState<string>(
    defaultValue ?? '',
  );

  const onSubmit = async ({
    options,
    placeholder,
    selectedOptionValue,
  }: SelectFormData) => {
    try {
      updateComponent(
        {
          pathParams: { procId: proc?.id!, stepId: selectedStep?.id! },
          body: {
            id,
            value: selectedOptionValue === '' ? null : selectedOptionValue,
            placeholder,
            componentName,
            component,
            options,
          },
        },
        {
          onSuccess: () => {
            setSelectedComponent(undefined);
            queryClient.invalidateQueries({
              queryKey: ['procs', proc?.id],
            });
          },
          onError: (error: any) => {
            showNotification({
              type: 'error',
              message: 'Update error',
              description: error.stack.message,
            });
            captureError(error, {
              module: 'select_edit_form_on_error_update_component',
              action: 'update_component',
            });
          },
        },
      );
    } catch (error) {
      captureError(error, {
        module: 'select_edit_form_on_error_update_component',
        action: 'update_component',
      });
    }
  };

  useImperativeHandle(ref, () => {
    return {
      submit: () => onSubmit({ options, placeholder, selectedOptionValue }),
      isLoading: updateComponentLoading,
    };
  }, [updateComponentLoading, options, placeholder, selectedOptionValue]);

  const createOption = (newOptionLabel: string) => {
    if (options.some((option) => option.label === newOptionLabel)) {
      showNotification({
        message: 'Select option creation failed',
        description: 'Option with identical label already exists.',
        type: 'warning',
      });
      return;
    }

    setOptions((prev) => [
      ...prev,
      {
        label: newOptionLabel,
        value: crypto.randomUUID(),
      },
    ]);
  };

  const deleteOption = (deletedOptionLabel: string) => {
    const newOptions = options.filter(
      (option) => option.label !== deletedOptionLabel,
    );

    setOptions(newOptions);

    if (!newOptions.some((option) => option.value === selectedOptionValue)) {
      setSelectedOptionValue('');
    }
  };

  return (
    <Form layout="vertical">
      <Form.Item label="Options">
        <Select
          data-testid="select-options-input"
          mode="tags"
          open={false}
          style={{ width: '100%' }}
          placeholder="Add select options here"
          onSelect={createOption}
          onDeselect={deleteOption}
          value={options.map(({ label }) => label)}
        />
      </Form.Item>
      <Form.Item label="Preselected option">
        <Select
          data-testid="default-value-input"
          style={{ width: '100%' }}
          value={selectedOptionValue}
          onSelect={setSelectedOptionValue}
          options={[{ label: '', value: '' }, ...options]}
        />
      </Form.Item>
      <Form.Item label="Placeholder text">
        <Input
          data-testid="placeholder-input"
          value={placeholder}
          onChange={(e) => setPlaceholder(e.target.value)}
        />
      </Form.Item>
    </Form>
  );
});
